import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, ArrowUpRight, CheckCircle, AlertCircle, Loader2, Calendar, Mail, Download } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inputClass =
  'w-full bg-transparent border-b py-3 text-white placeholder:text-text-secondary/40 focus:border-white/45 transition-colors duration-300 text-sm';
const inputStyle = { borderColor: 'rgba(255,255,255,0.14)' };

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [responseMessage, setResponseMessage] = useState('');

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const response = await axios.post(`${API_URL}/api/contact`, formData);
      setStatus('success');
      setResponseMessage(response.data.message);
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      setStatus('error');
      setResponseMessage(
        error.response?.data?.detail || 'Something went wrong. Please try again.'
      );
    }
  };

  return (
    <section
      id="contact"
      className="relative py-20 sm:py-28 md:py-40 overflow-hidden"
      data-testid="contact-section"
    >
      <div className="absolute top-0 left-0 right-0 h-px section-divider" />

      {/* Background accent */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 100%, rgba(112, 0, 255, 0.06) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Hero CTA text */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.85, ease: [0.25, 0.1, 0.25, 1] }}
          className="mb-20 md:mb-24"
        >
          <div className="section-label mb-8">Contact</div>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.92] mb-8">
            IF IT MATTERS,
            <br />
            <span className="gradient-text-brand">LET'S BUILD IT.</span>
          </h2>
          <p className="text-text-secondary text-lg md:text-xl max-w-xl leading-relaxed">
            Hiring, exploring a collaboration, or building something specific. I respond within 24 hours.
            Direct and specific works best.
          </p>
        </motion.div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-[1fr_1fr] gap-14 lg:gap-20 xl:gap-28">

          {/* Left: Form */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <h3 className="font-display text-2xl font-medium mb-8 tracking-tight">
              Send a Message
            </h3>

            <form onSubmit={handleSubmit} className="space-y-7" data-testid="contact-form">
              <div>
                <label htmlFor="name" className="block text-[10.5px] uppercase tracking-[0.24em] text-text-secondary mb-3 font-medium">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  style={inputStyle}
                  placeholder="Your name"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-[10.5px] uppercase tracking-[0.24em] text-text-secondary mb-3 font-medium">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  style={inputStyle}
                  placeholder="your@email.com"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-[10.5px] uppercase tracking-[0.24em] text-text-secondary mb-3 font-medium">
                  Message
                </label>
                {/* Textarea uses border-b to match inputs — consistent underline style */}
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full bg-transparent border-b py-3 text-white placeholder:text-text-secondary/40 focus:border-white/45 transition-colors duration-300 resize-none text-sm"
                  style={inputStyle}
                  placeholder="Tell me about the project, opportunity, or problem you're solving..."
                  data-testid="contact-message-input"
                />
              </div>

              {status === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 text-emerald-400 text-sm"
                  data-testid="contact-success-message"
                >
                  <CheckCircle className="w-4 h-4" />
                  {responseMessage}
                </motion.div>
              )}

              {status === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 text-red-400 text-sm"
                  data-testid="contact-error-message"
                >
                  <AlertCircle className="w-4 h-4" />
                  {responseMessage}
                </motion.div>
              )}

              <motion.button
                type="submit"
                disabled={status === 'loading'}
                className="group w-full flex items-center justify-center gap-2 py-4 bg-white text-black font-medium rounded-full hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                whileHover={{ scale: status === 'loading' ? 1 : 1.01 }}
                whileTap={{ scale: status === 'loading' ? 1 : 0.99 }}
                data-testid="contact-submit-btn"
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>

          {/* Right: Quick actions */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
            className="flex flex-col justify-center gap-5"
          >
            {/* Book a call — primary */}
            <motion.a
              href="https://calendly.com/rayhanpatel88/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="group block p-7 rounded-2xl border transition-all duration-300 glow-border"
              style={{
                background: 'linear-gradient(135deg, rgba(155,63,254,0.10) 0%, rgba(10,10,10,0.80) 100%)',
                borderColor: 'rgba(155,63,254,0.22)',
              }}
              whileHover={{ y: -4 }}
              data-testid="calendly-link"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(155,63,254,0.18)' }}
                  >
                    <Calendar className="w-4 h-4" style={{ color: '#9B3FFE' }} />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.24em] text-text-secondary font-medium">
                    Schedule
                  </span>
                </div>
                <ArrowUpRight className="w-5 h-5 text-text-secondary group-hover:text-white transition-colors" />
              </div>
              <h4 className="font-display text-xl font-medium mb-2 tracking-tight">
                Book a 30-min Call
              </h4>
              <p className="text-text-secondary text-sm leading-relaxed">
                Let's discuss your project, AI strategy, or what you're trying to build. Focused, no fluff.
              </p>
            </motion.a>

            {/* Email */}
            <motion.a
              href="mailto:rayhanpatel88@gmail.com"
              className="group block p-7 rounded-2xl border transition-all duration-300 glow-border"
              style={{ background: 'rgba(10,10,10,0.60)', borderColor: 'rgba(255,255,255,0.07)' }}
              whileHover={{ y: -4 }}
              data-testid="email-link"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.06)' }}
                  >
                    <Mail className="w-4 h-4 text-text-secondary" />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.24em] text-text-secondary font-medium">
                    Direct
                  </span>
                </div>
                <ArrowUpRight className="w-5 h-5 text-text-secondary group-hover:text-white transition-colors" />
              </div>
              <h4 className="font-display text-xl font-medium mb-2 tracking-tight">
                rayhanpatel88@gmail.com
              </h4>
              <p className="text-text-secondary text-sm leading-relaxed">
                Prefer email? Drop a line and I'll respond within 24 hours.
              </p>
            </motion.a>

            {/* Download CV */}
            <motion.a
              href="/Rayhan_Patel_CV.pdf"
              download="Rayhan_Patel_CV.pdf"
              className="group flex items-center justify-between p-5 rounded-2xl border transition-all duration-300 glow-border"
              style={{ background: 'rgba(10,10,10,0.60)', borderColor: 'rgba(255,255,255,0.07)' }}
              whileHover={{ y: -2 }}
              data-testid="download-cv-link"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(255,255,255,0.06)' }}
                >
                  <Download className="w-4 h-4 text-text-secondary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Download CV</p>
                  <p className="text-[11px] text-text-secondary">PDF · Updated 2026</p>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-text-secondary group-hover:text-white transition-colors" />
            </motion.a>

            {/* Location note */}
            <div className="px-2 py-2">
              <p className="text-[10px] uppercase tracking-[0.24em] text-text-secondary font-medium mb-2">
                Based in
              </p>
              <p className="font-display text-lg font-medium">Leicester, UK</p>
              <p className="text-text-secondary text-sm mt-1">
                Open to remote worldwide · Available now
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
